/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Dropzone components
import Dropzone from "dropzone";

// Dropzone styles
import "dropzone/dist/dropzone.css";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";

// Custom styles for the MDDropzone
import MDDropzoneRoot from "components/MDDropzone/MDDropzoneRoot";

// Material Dashboard 3 PRO React context
import { useMaterialUIController } from "context";

const MDDropzone = forwardRef(({ options, onChange }, ref) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const dropzoneRef = useRef();
  const dzInstanceRef = useRef();

  useEffect(() => {
    Dropzone.autoDiscover = false;

    function createDropzone() {
      const dz = new Dropzone(dropzoneRef.current, { ...options });
      dzInstanceRef.current = dz;
      dz.on("addedfile", () => {
        if (typeof onChange === "function") {
          onChange(dz.files);
        }
      });
      dz.on("removedfile", () => {
        if (typeof onChange === "function") {
          onChange(dz.files);
        }
      });
      return dz;
    }

    function removeDropzone() {
      if (Dropzone.instances.length > 0)
        Dropzone.instances.forEach((dz) => dz.destroy());
    }

    createDropzone();

    return () => removeDropzone();
  }, [options, onChange]);

  // Expose Dropzone instance via ref
  useImperativeHandle(
    ref,
    () => ({
      dropzone: dzInstanceRef.current,
    }),
    []
  );

  return (
    <MDDropzoneRoot
      component="form"
      action="/file-upload"
      ref={dropzoneRef}
      className="form-control dropzone"
      ownerState={{ darkMode }}
    >
      <MDBox className="fallback" bgColor="transparent">
        <MDBox component="input" name="file" type="file" multiple />
      </MDBox>
    </MDDropzoneRoot>
  );
});

// Typechecking props for the MDDropzone
MDDropzone.propTypes = {
  options: PropTypes.objectOf(PropTypes.any).isRequired,
  onChange: PropTypes.func,
};

export default MDDropzone;
